import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
  GridRowsProp,
  GridRowModesModel,
  GridRowModes,
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowEditStopReasons,
} from '@mui/x-data-grid';
import { Toolbar, Typography, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper } from '@mui/material';
import { Role } from '../../interface/Types';
import { useAuth } from '../../hooks/useAuth';

const initialRows: GridRowsProp = [
  {
    id: 1,
    year: '2022',
    month: '2',
    level: '4',
    paper: '3',
    accuracyRate: '58',
  },
  {
    id: 2,
    year: '2232',
    month: '4',
    level: '4',
    paper: '3',
    accuracyRate: '45',
  },
];

const initialQuestionsRows: GridRowsProp = [
  {
    id: 1,
    question: 'Direction: For this part, you are allowed 30 minutes to write a short essay on how to maintain a healthy lifestyle. You should write at least 120 words but no more than 180 words.',
    optionA: '本文的中心话题是什么？',
    optionB: 'discussing how to deal with students\' learning pressure',
    optionC: 'dicussing the importance of a healthy diet',
    optionD: 'discussing how to maintain a healthy lifestyle',
    isMultipleChoice: false,
    type: '写作逻辑',
    questionType: '写译',
    answer: 'C',
    explanation: '',
    commonMistake: '',
  },
  {    
    id: 2,
    question: 'Direction: For this part, you are allowed 30 minutes to write a short essay on how to maintain a healthy lifestyle. You should write at least 120 words but no more than 180 words.',
    optionA: '题目中的“healthy lifestyle”是什么意思？',
    optionB: '健康的身体',
    optionC: '健康的生活方式',
    optionD: '健康地生活',
    isMultipleChoice: false,
    type: '写作表达',
    questionType: '写译',
    answer: 'B',
    explanation: '',
    commonMistake: '',
  }
];

const initialQuestionsColumns: GridColDef[] = [
  { field: 'question', headerName: '题干', width: 300, editable: true },
  { field: 'optionA', headerName: '选项A', width: 200, editable: true },
  { field: 'optionB', headerName: '选项B', width: 200, editable: true },
  { field: 'optionC', headerName: '选项C', width: 200, editable: true },
  { field: 'optionD', headerName: '选项D', width: 200, editable: true },
  { field: 'optionE', headerName: '选项E', width: 200, editable: true },
  { field: 'isMultipleChoice', headerName: '是否多选', width: 150, editable: true, type: 'boolean' },
  { field: 'type', headerName: '类型', width: 150, editable: true },
  { field: 'questionType', headerName: '题型', width: 150, editable: true },
  { field: 'article', headerName: '文章', width: 300, editable: true },
  { field: 'answer', headerName: '答案', width: 100, editable: true },
  { field: 'explanation', headerName: '解析', width: 300, editable: true },
  { field: 'commonMistake', headerName: '易错项', width: 150, editable: true, type: 'singleSelect', valueOptions: ['A','B','C','D'] },
];

const Writing: React.FC = () => {
  const [rows, setRows] = React.useState(initialRows);
  const [questions, setQuestions] = React.useState(initialQuestionsRows);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
  const [open, setOpen] = React.useState(false);
  const [questionDetailOpen, setQuestionDetailOpen] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState<GridRowId | null>(null);
  const [updateQuestionGrid, setupdateQuestionGrid] = React.useState<GridRowsProp>([]);
  const { user } = useAuth();

  const handleAlert = (id: GridRowId) => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleClose = (confirmed: boolean) => {
    if (confirmed && selectedId !== null) {
      setRows(rows.filter((row) => row.id !== selectedId));
    }
    setOpen(false);
    setSelectedId(null);
  };

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    if (user?.role === Role.guest) {
      alert('You do not have permission to edit.');
      return;
    }
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    if (user?.role === Role.guest) {
      alert('You do not have permission to save changes.');
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View, ignoreModifications: true } });
      return;
    }
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    if (user?.role !== Role.admin) {
      alert('You do not have permission to delete.');
      return;
    }
    handleAlert(id);
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow!.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const processQuestionRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };
    console.log('updatedRow', updatedRow);
    setQuestions(questions.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleAddClick = () => {
    if (user?.role !== Role.admin) {
      alert('You do not have permission to add.');
      return;
    }

    const id = rows.length ? rows[rows.length - 1].id + 1 : 1;
    //setQuestionDetailOpen(true);
    setRows([...rows, { id, year: 'NA', month: 'NA', level: 'NA', paper: 'NA', question: '', optionA: '', optionB: '', optionC: '', optionD: '', isMultipleChoice: false, type: '', questionType: '', answer: '', explanation: '', commonMistake: '', accuracyRate: '', isNew: true }]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'RoleName' },
    }));
  };

  const handleDetailClick = (id: number) => {
    let filteredQuestion = questions.filter((question) => question.id === id);
    if (filteredQuestion.length === 0) {
        console.log('No question found');
        filteredQuestion = [{
            id: id,
            question: '',
            optionA: '',
            optionB: '',
            optionC: '',
            optionD: '',
            optionE: '',
            isMultipleChoice: false,
            type: '',
            questionType: '',
            article: '',
            answer: '',
            explanation: '',
            commonMistake: ''
          }];
        }
    setupdateQuestionGrid(filteredQuestion);
    setQuestionDetailOpen(true);
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'year', headerName: '年', width: 100, editable: true, type: 'singleSelect', valueOptions: [2005,2006,2007,2008,2009,2010,2011,2012,2013,2014,2015,2016,2017,2018,2019,2020,2021,2022,2023,2024,2025,2026,2027,2028 ] },
    { field: 'month', headerName: '月', width: 100, editable: true ,type: 'singleSelect', valueOptions: [1,2,3,4,5,6,7,8,9,10,11,12] },
    { field: 'level', headerName: '级别', width: 100, editable: true ,type: 'singleSelect', valueOptions: [4,6]},
    { field: 'paper', headerName: '卷', width: 100, editable: true, type: 'singleSelect', valueOptions: [1,2,3,4,5,6,7,8,9,10] },
    { field: 'accuracyRate', headerName: '全站正确率(%)', width: 150, editable: true, type: 'number'},
    { 
      field: 'question_detial', 
      headerName: '题目详情', 
      width: 100, 
      editable: false,
      renderCell: (params) => {
        const id = params.row.id;
        return <Button onClick={() => handleDetailClick(id)}>查看</Button>;
      }
    },
    {
      field: '修改/删除',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              key={`save-${id}`}
              label="Save"
              sx={{ color: 'primary.main' }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              key={`cancel-${id}`}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            key={`edit-${id}`}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            key={`delete-${id}`}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Toolbar>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          写作题目管理
        </Typography>
        <Button color="primary" startIcon={<AddIcon />} onClick={handleAddClick}>
          添加新的写作题目
        </Button>
      </Toolbar>
      <Paper sx={{ width: '100%', overflowX: 'auto' }}>
        <Box sx={{ minWidth: 1200, height: 500 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            onRowModesModelChange={handleRowModesModelChange}
            getRowId={(row) => row.id}
          />
        </Box>
      </Paper>
      <Dialog open={questionDetailOpen} onClose={() => setQuestionDetailOpen(false)} maxWidth="xl" fullWidth>
    <DialogTitle>
     <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="h6">题目详情</Typography>
      <Button onClick={() => setQuestionDetailOpen(false)} color="primary" sx={{ padding: '10px 20px', fontSize: '1rem' }}>
        保存
      </Button>
    </Box>
    </DialogTitle>    
        <Paper sx={{ overflowX: 'auto' }}>
          <Box sx={{ width: '100%', height: 800 }}>
            <DataGrid
              rows={updateQuestionGrid}
              columns={initialQuestionsColumns}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={processQuestionRowUpdate}
              onRowModesModelChange={handleRowModesModelChange}
              getRowId={(row) => row.id}
            />
          </Box>
        </Paper>

      </Dialog>
      <Dialog open={open} onClose={() => handleClose(false)}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <DialogContentText>你确定要删除这个题目吗？此操作无法撤销。</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleClose(false)}>取消</Button>
          <Button onClick={() => handleClose(true)} color="primary" autoFocus>
            确认
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Writing;
